import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Star, GraduationCap, Shield, Users, Briefcase, Mail, User } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const FOUNDER = {
  name: "Reena Srivastava",
  role: "Founder",
  bio: "A visionary leader driving digital transformation for Indian SMEs with passion, integrity and over two decades of experience in Tally implementation and business consulting.",
  image: "/reena.jpg",
};

const CEO = {
  name: "Harish Srivastava",
  role: "CEO",
  bio: "Leading operations and customer success with a hands-on approach to TallyPrime consulting, enterprise deployments and long-term client relationships.",
  image: "/harish.jpg",
};

const TEAM = [
  { name: "Abhay" },
  { name: "Krishna" },
  { name: "Rahul" },
];

const VALUES = [
  { Icon: Heart, title: "Customer First", desc: "Every decision starts with how does this help our clients." },
  { Icon: Shield, title: "Integrity", desc: "Transparent pricing, honest advice, zero hidden costs." },
  { Icon: Star, title: "Excellence", desc: "We don't settle — every implementation is benchmarked." },
  { Icon: GraduationCap, title: "Continuous Learning", desc: "Our team trains 200+ hours per year on Tally updates." },
];

export default function Team() {
  return (
    <div className="relative bg-[#FAF9F6]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0D47A1] text-white overflow-hidden circuit-pattern">
        <div className="orb bg-[#FFA000] w-[300px] h-[300px] -top-20 -right-20 opacity-15" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div variants={stagger(0.1, 0.08)} initial="hidden" animate="show">
            <motion.h1 variants={fadeUp} className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Meet Our <span className="text-[#FFA000]">Team</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-4 text-lg text-white/75 max-w-xl">
              The people behind the technology — dedicated experts powering businesses across India.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            variants={stagger(0.1, 0.08)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-12"
          >
            <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Leadership</motion.span>
            <motion.h2 variants={fadeUp} className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
              The minds <span className="font-serif-accent text-[#FFA000]">behind our vision.</span>
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Founder */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col sm:flex-row items-center gap-6 bg-white rounded-2xl border border-[#0D47A1]/8 shadow-[0_20px_60px_rgba(13,71,161,0.08)] overflow-hidden"
            >
              <div className="sm:w-56 w-full h-72 sm:h-64 shrink-0 relative overflow-hidden">
                <img
                  src={FOUNDER.image}
                  alt={FOUNDER.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 sm:p-8 flex-1">
                <h3 className="font-display font-black text-2xl text-[#1A1A2E]">{FOUNDER.name}</h3>
                <p className="text-base font-semibold text-[#FFA000] mt-1">{FOUNDER.role}</p>
                <p className="mt-3 text-[15px] text-[#4A5568] leading-relaxed">{FOUNDER.bio}</p>
                <div className="mt-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#FFA000]/10 flex items-center justify-center">
                    <Briefcase className="w-4 h-4 text-[#FFA000]" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#1A1A2E]">20+ Years</div>
                    <div className="text-xs text-[#8896A6]">Industry Experience</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* CEO */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="flex flex-col sm:flex-row items-center gap-6 bg-white rounded-2xl border border-[#0D47A1]/8 shadow-[0_20px_60px_rgba(13,71,161,0.08)] overflow-hidden"
            >
              <div className="sm:w-56 w-full h-72 sm:h-64 shrink-0 relative overflow-hidden">
                <img
                  src={CEO.image}
                  alt={CEO.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 sm:p-8 flex-1">
                <h3 className="font-display font-black text-2xl text-[#1A1A2E]">{CEO.name}</h3>
                <p className="text-base font-semibold text-[#FFA000] mt-1">{CEO.role}</p>
                <p className="mt-3 text-[15px] text-[#4A5568] leading-relaxed">{CEO.bio}</p>
                <div className="mt-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#FFA000]/10 flex items-center justify-center">
                    <Briefcase className="w-4 h-4 text-[#FFA000]" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#1A1A2E]">20+ Years</div>
                    <div className="text-xs text-[#8896A6]">Industry Experience</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div variants={stagger(0.1, 0.08)} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}>
            <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Our People</motion.span>
            <motion.h2 variants={fadeUp} className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
              The <span className="font-serif-accent text-[#0D47A1]">team.</span>
            </motion.h2>
          </motion.div>

          <motion.div
            variants={stagger(0.08, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {TEAM.map((member) => (
              <motion.div
                key={member.name}
                variants={fadeUp}
                whileHover={{ y: -4 }}
                className="group rounded-2xl bg-[#FAF9F6] border border-[#0D47A1]/8 overflow-hidden hover:border-[#FFA000]/30 transition-all duration-500"
              >
                {/* Blank photo placeholder */}
                <div className="relative h-52 bg-[#F0F2F5] flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-[#E2E6EC] flex items-center justify-center">
                    <User className="w-10 h-10 text-[#B0B8C4]" />
                  </div>
                </div>
                <div className="p-4 text-center">
                  <h4 className="font-display font-bold text-lg text-[#1A1A2E]">{member.name}</h4>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div variants={stagger(0.1, 0.08)} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} className="text-center max-w-2xl mx-auto">
            <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Our Values</motion.span>
            <motion.h2 variants={fadeUp} className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
              What <span className="font-serif-accent text-[#FFA000]">drives us.</span>
            </motion.h2>
          </motion.div>

          <motion.div
            variants={stagger(0.08, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {VALUES.map(({ Icon, title, desc }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                whileHover={{ y: -4 }}
                className="rounded-2xl bg-white border border-[#0D47A1]/8 p-7 text-center hover:shadow-[0_20px_50px_rgba(13,71,161,0.08)] transition-all duration-500"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#FFA000]/10 flex items-center justify-center mx-auto mb-5">
                  <Icon className="w-6 h-6 text-[#FFA000]" />
                </div>
                <h4 className="font-display font-bold text-lg text-[#1A1A2E]">{title}</h4>
                <p className="mt-2 text-[14px] text-[#4A5568] leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-20 bg-[#0D47A1] text-white text-center circuit-pattern relative overflow-hidden">
        <div className="orb bg-[#FFA000] w-[300px] h-[300px] top-10 right-10 opacity-10" />
        <div className="max-w-3xl mx-auto px-6 lg:px-8 relative z-10">
          <h2 className="font-display font-black text-4xl sm:text-5xl leading-[1.08]">
            Want to <span className="text-[#FFA000]">join us?</span>
          </h2>
          <p className="mt-4 text-lg text-white/75">We are always looking for talented people who love technology and customer service.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="mailto:careers@tallypro.in" className="inline-flex items-center gap-2 rounded-lg bg-[#FFA000] hover:bg-[#E69000] text-white px-7 h-13 font-bold shadow-[0_15px_40px_rgba(255,160,0,0.3)] transition">
              <Mail className="w-4 h-4" /> Get in Touch
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
